import type { FetchOptions } from 'ofetch'

interface ApiOptions extends Omit<FetchOptions, 'method' | 'body'> {}

export const useApi = () => {
  const config = useRuntimeConfig()
  const { $refreshToken, $logout } = useNuxtApp()
  const apiBase = config.public.apiBase as string
  
  const apiCall = async <T = any>(
    url: string, 
    options: any = {}
  ): Promise<T> => {
    const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`
    
    try {
      const response = await $fetch<T>(fullUrl, {
        ...options,
        credentials: 'include',
        onResponseError({ response }) {
          if (response.status === 401) {
            // Will be handled in catch block
            throw new Error('Unauthorized')
          }
        },
      })
      
      return response
    } catch (error: any) {
      // Handle 401 errors
      if (error?.data?.statusCode === 401 || error?.response?.status === 401 || 
          error?.message === 'Unauthorized') {
        // Try to refresh token
        const refreshed = await $refreshToken()
        if (refreshed) {
          // Retry the request
          return await $fetch<T>(fullUrl, {
            ...options,
            credentials: 'include',
          })
        } else {
          // Refresh failed, logout
          await $logout()
          throw error
        }
      }
      throw error
    }
  }
  
  return {
    get: <T = any>(url: string, options?: ApiOptions) => 
      apiCall<T>(url, { ...options, method: 'GET' }),
    
    post: <T = any>(url: string, body?: any, options?: ApiOptions) => 
      apiCall<T>(url, { ...options, method: 'POST', body }),
    
    put: <T = any>(url: string, body?: any, options?: ApiOptions) => 
      apiCall<T>(url, { ...options, method: 'PUT', body }),
    
    patch: <T = any>(url: string, body?: any, options?: ApiOptions) => 
      apiCall<T>(url, { ...options, method: 'PATCH', body }),
    
    delete: <T = any>(url: string, options?: ApiOptions) => 
      apiCall<T>(url, { ...options, method: 'DELETE' }),
  }
}